import { CarIcon, InfoIcon, UsersThreeIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { EntityKind, IdentityToken } from '@/types/identity';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { EntityAvatar } from '@/components/EntityAvatar/EntityAvatar';
import {
    IDENTITY_CELL_BUTTON_CLASSES,
    IDENTITY_CELL_SQUARE_CLASSES,
    type IdentityAvatarSize,
} from './identityAvatarSize';

const UNDEFINED_ICONS = {
    person: UsersThreeIcon,
    vehicle: CarIcon,
} as const;

interface UndefinedIdentityCellProps {
    token: IdentityToken;
    kind: EntityKind;
    label: string;
    description: string;
    count: number;
    isSelected: boolean;
    showLabel: boolean;
    size: IdentityAvatarSize;
    onToggle: () => void;
}

export function UndefinedIdentityCell({
    token,
    kind,
    label,
    description,
    count,
    isSelected,
    showLabel,
    size,
    onToggle,
}: UndefinedIdentityCellProps) {
    const Icon = UNDEFINED_ICONS[kind];
    const descriptionTooltipContent = (
        <TooltipContent
            variant="panel"
            className="w-[315px] p-2 text-xs leading-[18px] text-grey-200"
        >
            {description}
        </TooltipContent>
    );

    const cell = (
        <button
            type="button"
            onClick={onToggle}
            aria-pressed={isSelected}
            aria-label={`${label} (${count})`}
            data-testid={`identity-cell-${token}`}
            className={cn(
                IDENTITY_CELL_BUTTON_CLASSES,
                showLabel ? 'w-full' : IDENTITY_CELL_SQUARE_CLASSES,
            )}
        >
            <EntityAvatar
                icon={<Icon weight="regular" />}
                shape="circle"
                size={size}
                selectionState={isSelected ? 'selected' : 'unselected'}
                className="bg-grey-400 text-grey-100"
            />
            {showLabel && (
                <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-center text-sm font-normal text-white-100">
                        {label}
                    </span>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <InfoIcon
                                weight="fill"
                                className="size-3.5 shrink-0 text-grey-300"
                            />
                        </TooltipTrigger>
                        {descriptionTooltipContent}
                    </Tooltip>
                </span>
            )}
        </button>
    );

    if (showLabel) {
        return cell;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{cell}</TooltipTrigger>
            {descriptionTooltipContent}
        </Tooltip>
    );
}
